import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type { ProgressState, ExperimentProgress, CourseProgress } from '@/types';
import { progressService } from '@/services';

interface ProgressContextValue {
  state: ProgressState;
  markStepComplete: (courseId: string, experimentId: string, stepId: string) => void;
  markPartComplete: (courseId: string, experimentId: string, partId: string) => void;
  getExperimentProgress: (courseId: string, experimentId: string) => ExperimentProgress | null;
  getCourseProgress: (courseId: string) => number;
  resetExperiment: (courseId: string, experimentId: string) => void;
}

const ProgressContext = createContext<ProgressContextValue | null>(null);

const initialState: ProgressState = {
  courses: {},
};

const STORAGE_KEY = 'virtuallab_progress';
const USE_BACKEND = import.meta.env.VITE_USE_BACKEND === 'true';

function createEmptyExperimentProgress(): ExperimentProgress {
  return {
    partsCompleted: [],
    lastAccessed: new Date().toISOString(),
    totalStepsCompleted: 0,
  };
}

function createEmptyCourseProgress(): CourseProgress {
  return {
    experiments: {},
    overallProgress: 0,
  };
}

export function ProgressProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ProgressState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return { ...initialState, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Failed to load progress state:', e);
    }
    return initialState;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const ensureCourseExists = (courses: ProgressState['courses'], courseId: string): CourseProgress => {
    if (!courses[courseId]) {
      courses[courseId] = createEmptyCourseProgress();
    }
    return courses[courseId];
  };

  const ensureExperimentExists = (course: CourseProgress, experimentId: string): ExperimentProgress => {
    if (!course.experiments[experimentId]) {
      course.experiments[experimentId] = createEmptyExperimentProgress();
    }
    return course.experiments[experimentId];
  };

  // Extract experiment number from experimentId (e.g., 'exp3' -> 3)
  const getExpNumber = (experimentId: string): number => {
    return parseInt(experimentId.replace('exp', ''));
  };

  const markStepComplete = useCallback((courseId: string, experimentId: string, stepId: string) => {
    setState(prev => {
      const newCourses = { ...prev.courses };
      const course = ensureCourseExists(newCourses, courseId);
      const experiment = ensureExperimentExists(course, experimentId);

      experiment.totalStepsCompleted += 1;
      experiment.lastAccessed = new Date().toISOString();

      return { courses: newCourses };
    });

    // Sync with backend
    if (USE_BACKEND) {
      const parts = stepId.split('-');
      const partId = parseInt(parts[0]) || 1;
      const stepNum = parseInt(parts[1]) || 1;
      progressService.updateProgress(courseId, getExpNumber(experimentId), partId, stepNum, true).catch(() => {
        // Silently fail - local storage is the source of truth
      });
    }
  }, []);

  const markPartComplete = useCallback((courseId: string, experimentId: string, partId: string) => {
    setState(prev => {
      const newCourses = { ...prev.courses };
      const course = ensureCourseExists(newCourses, courseId);
      const experiment = ensureExperimentExists(course, experimentId);

      if (!experiment.partsCompleted.includes(partId)) {
        experiment.partsCompleted = [...experiment.partsCompleted, partId];
      }
      experiment.lastAccessed = new Date().toISOString();

      // Update course progress
      const totalExperiments = 11;
      const completedExperiments = Object.values(course.experiments).filter(
        exp => exp.partsCompleted.length >= 2
      ).length;
      course.overallProgress = Math.round((completedExperiments / totalExperiments) * 100);

      return { courses: newCourses };
    });

    // Sync with backend
    if (USE_BACKEND) {
      const partNum = parseInt(partId) || 1;
      progressService.updateProgress(courseId, getExpNumber(experimentId), partNum, 0, true).catch(() => {
        // Silently fail
      });
    }
  }, []);

  const getExperimentProgress = useCallback((courseId: string, experimentId: string): ExperimentProgress | null => {
    return state.courses[courseId]?.experiments[experimentId] || null;
  }, [state]);

  const getCourseProgress = useCallback((courseId: string): number => {
    return state.courses[courseId]?.overallProgress || 0;
  }, [state]);

  const resetExperiment = useCallback((courseId: string, experimentId: string) => {
    setState(prev => {
      const newCourses = { ...prev.courses };
      if (newCourses[courseId]?.experiments[experimentId]) {
        newCourses[courseId].experiments[experimentId] = createEmptyExperimentProgress();
      }
      return { courses: newCourses };
    });

    // Sync with backend
    if (USE_BACKEND) {
      progressService.resetExperimentProgress(courseId, getExpNumber(experimentId)).catch(() => {
        // Silently fail
      });
    }
  }, []);

  const value: ProgressContextValue = {
    state,
    markStepComplete,
    markPartComplete,
    getExperimentProgress,
    getCourseProgress,
    resetExperiment,
  };

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
}

export function useProgress() {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
}

export default ProgressContext;
